<template>
  <el-dialog custom-class="management-dialog" :visible="visible" title="从项目模板创建项目" width="min(560px, calc(100vw - 24px))" append-to-body :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close">
    <p v-if="current">{{ current.name }} · v{{ current.version }} · {{ current.snapshot.pages.length }} 个页面。全部页面、首页及内部跳转将复制到新大屏。</p>
    <el-form ref="form" :model="form" :rules="rules" label-position="top" :disabled="saving || loading">
      <el-form-item label="项目类型"><el-select popper-class="management-dialog-options" v-model="projectType" :disabled="uncertain" style="width: 100%" @change="changeType"><el-option label="大屏" value="big_screen" /><el-option label="2D组态" value="scada2d" /></el-select></el-form-item>
      <el-form-item label="项目名称" prop="name"><el-input v-model="form.name" maxlength="128" /></el-form-item>
      <el-form-item label="项目分类" prop="catalogId"><el-select popper-class="management-dialog-options" v-model="form.catalogId" filterable style="width: 100%" placeholder="选择当前类型的项目分类"><el-option v-for="category in categories" :key="category.id" :label="category.label" :value="category.id" /></el-select></el-form-item>
      <el-form-item label="说明"><el-input v-model="form.description" type="textarea" maxlength="1000" /></el-form-item>
    </el-form>
    <p v-if="error" role="alert">{{ error }}</p>
    <el-button v-if="readError" type="text" @click="open">重新加载</el-button>
    <span slot="footer"><el-button :disabled="saving" @click="close">{{ uncertain ? '关闭并检查项目列表' : '取消' }}</el-button><el-button type="primary" :loading="saving" :disabled="loading || readError || uncertain || !canCreate || !current || !categories.length" @click="save">创建并编辑{{ projectLabel }}</el-button></span>
  </el-dialog>
</template>
<script>
import store from '@/store'
import { getTemplate } from '@/api/templates'
import { queryTree } from '@/api/category'
import { addObj } from '@/api/visual'
import { templateResult, normalizeTemplate, templateError } from '@/utils/templates'
import { projectTemplateCreatePayload, confirmProjectCreated } from '@/editor/core/project-template-create'
import { projectCategories, projectTypeName } from '@/utils/project-types'
import { screenCategoryOptions } from '@/utils/screen-management'
export default {
  props: { visible: Boolean, template: Object },
  data: () => ({ current: null, categoryTree: [], categories: [], projectType: 'big_screen', loading: false, saving: false, readError: false, uncertain: false, error: '', form: { name: '', catalogId: '', description: '' },
    rules: { name: [{ required: true, whitespace: true, message: '请输入大屏名称', trigger: 'blur' }], catalogId: [{ required: true, message: '请选择大屏分类', trigger: 'change' }] } }),
  computed: { projectLabel () { return projectTypeName(this.projectType) }, canCreate () { return store.getters['auth/isAuth']('kydz/View/Data:add') && store.getters['auth/hasResourcePermission']('visualizationTemplate:query') } },
  watch: { visible (value) { if (value) this.open(); else this._read++ }, canCreate (value) { if (!value && !this.saving) this.close() } },
  created () { this._read = 0; if (this.visible) this.open() },
  beforeDestroy () { this._read++ },
  methods: {
    changeType () {
      this.form.catalogId = ''
      this.categories = screenCategoryOptions(projectCategories(this.categoryTree, this.projectType))
      this.error = this.categories.length ? '' : '请先在' + this.projectLabel + '管理中新增分类'
    },
    async open () {
      const ticket = ++this._read
      if (!this.canCreate || !this.template) return
      this.loading = true; this.readError = false; this.uncertain = false; this.error = ''; this.current = null; this.categories = []
      this.form = { name: this.template.name + '-副本', catalogId: '', description: this.template.description || '' }
      try {
        const results = await Promise.allSettled([getTemplate(this.template.id), queryTree({})])
        if (ticket !== this._read || this._isDestroyed || !this.canCreate) return
        const failed = results.find(result => result.status === 'rejected')
        if (failed) throw failed.reason
        const current = normalizeTemplate(templateResult(results[0].value))
        if (current.kind !== 'project' || current.id !== this.template.id) throw new Error('模板类型或编号与请求不一致')
        const tree = templateResult(results[1].value)
        screenCategoryOptions(tree)
        this.categoryTree = tree; this.current = current; this.changeType()
      } catch (error) { if (ticket === this._read && !this._isDestroyed) { this.readError = true; this.error = templateError(error) } }
      finally { if (ticket === this._read && !this._isDestroyed) this.loading = false }
    },
    close () { if (!this.saving) this.$emit('update:visible', false) },
    async save () {
      if (!this.canCreate || this.saving || this.loading || this.uncertain || this.readError || !this.current) return
      let valid = false
      valid = await new Promise(resolve => this.$refs.form.validate(resolve))
      if (!valid || !this.visible || this._isDestroyed || !this.canCreate || this.saving || this.uncertain) return
      if (!this.categories.some(category => category.id === this.form.catalogId)) { this.error = '请选择可用的大屏分类'; return }
      let payload
      try { payload = projectTemplateCreatePayload(this.current, this.form, undefined, this.projectType) } catch (error) { this.error = error.message; return }
      this.saving = true; this.error = ''
      try {
        const response = await addObj(payload)
        this.uncertain = true
        const created = confirmProjectCreated(response, this.projectType)
        if (this._isDestroyed) return
        this.uncertain = false; this.$emit('created', created.id); this.$emit('update:visible', false)
        this.$message.success('大屏已创建，全部页面和数据配置已复制')
        if (this.canCreate) this.$router.push({ name: 'build', params: { id: created.id } }).catch(error => { if (error.name !== 'NavigationDuplicated') this.$message.error('大屏已创建，但编辑器未能打开，请从大屏列表进入') })
      } catch (error) {
        if (!this._isDestroyed) { if (!error.status || error.status >= 500) this.uncertain = true; this.error = this.uncertain ? '创建结果未确认，请到大屏列表检查，勿重复提交' : templateError(error) }
      } finally { if (!this._isDestroyed) this.saving = false }
    }
  }
}
</script>
